import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import SuccessPopup from '../shared/SuccessPopup';
import { confirmUngeocodedMapAddressStandaloneIfNeeded } from '../../utils/mapSubmitGuard';
import { getClientMetadata } from '../../utils/signatureMetadata';

const EditStudentForm = () => {
  const navigate = useNavigate();
  const { studentIntakeId } = useParams();

  // Student and form list state
  const [studentIntake, setStudentIntake] = useState(null);
  const [submittedForms, setSubmittedForms] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);

  // Individual form editing state
  const [selectedForm, setSelectedForm] = useState(null);
  const [currentFormDefinition, setCurrentFormDefinition] = useState(null);
  const [currentFormData, setCurrentFormData] = useState({});
  const [currentSubmissionId, setCurrentSubmissionId] = useState(null);

  // Modal state for adding new forms
  const [showAddFormModal, setShowAddFormModal] = useState(false);
  const [modalFormInstance, setModalFormInstance] = useState(null);
  const [modalFormDefinition, setModalFormDefinition] = useState(null);
  const [modalFormData, setModalFormData] = useState({});
  const [savingModal, setSavingModal] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveFeedback, setSaveFeedback] = useState(null);

  // Debug selectedForm changes
  useEffect(() => {
    console.log('selectedForm changed to:', selectedForm);
  }, [selectedForm]);

  // Load student info and submitted forms
  useEffect(() => {
    const loadStudentData = async () => {
      if (!studentIntakeId) return;

      try {
        setLoading(true);

        // Load student intake info (district admin endpoint)
        console.log('Loading student intake for ID:', studentIntakeId);
        const studentResponse = await axios.get(`/api/student-intake/district/${studentIntakeId}`);
        console.log('Student response:', studentResponse);
        if (studentResponse.data) {
          setStudentIntake(studentResponse.data);
        }

        // Load submitted forms for this student (district admin endpoint)
        console.log('Loading forms for student ID:', studentIntakeId);
        const formsResponse = await axios.get(`/api/student-intake/district/${studentIntakeId}/forms`);
        console.log('Raw forms response data:', formsResponse.data);
        // Filter out helper forms - they should only exist in new student context
        const filteredForms = (formsResponse.data || []).filter(form => {
          const formName = (form.instance_name || form.master_form_name || '').toLowerCase();
          const shouldInclude = !formName.includes('helper') && !formName.includes('information helper');
          console.log('Form:', formName, 'Include:', shouldInclude);
          return shouldInclude;
        });
        console.log('Filtered forms:', filteredForms);
      setSubmittedForms(filteredForms);

        // Load all available forms for potential addition
        console.log('Loading available forms...');
        const availableResponse = await axios.get('/api/form-instances/district');
        console.log('Available forms response:', availableResponse.data);
        // Filter out helper forms - they should only exist in new student context
        const filteredAvailableForms = (availableResponse.data || []).filter(form => {
          const formName = (form.instance_name || form.master_form_name || '').toLowerCase();
          const shouldInclude = !formName.includes('helper') && !formName.includes('information helper');
          return shouldInclude;
        });
        console.log('Filtered available forms:', filteredAvailableForms);
        setAvailableForms(filteredAvailableForms);

      } catch (error) {
        console.error('Failed to load student data:', error);
        setError('Failed to load student information');
      } finally {
        setLoading(false);
      }
    };

    loadStudentData();
  }, [studentIntakeId]);

  // Debug submitted forms when they change
  useEffect(() => {
    if (submittedForms.length > 0) {
      console.log('=== EDIT STUDENT FORM - FIELD COUNT DEBUG ===');
      submittedForms.forEach((form, index) => {
        const fieldCount = form.form_data ? Object.keys(form.form_data).length : 0;
        console.log(`Submitted Form ${index}: ${form.instance_name || form.master_form_name} - ${fieldCount} fields`);
        if (fieldCount === 21) {
          console.log('🚨 ALERT: This submitted form has exactly 21 fields!');
          console.log('Submitted form data keys:', Object.keys(form.form_data));
        }
        if (fieldCount < 21) {
          console.log('✅ Normal field count for submitted form:', form.instance_name || form.master_form_name);
        }
      });
      console.log('=== END EDIT STUDENT FORM - FIELD COUNT DEBUG ===');
    }
  }, [submittedForms]);

  // Handle form selection for editing
  const selectForm = async (form) => {
    try {
      setLoading(true);
      setSelectedForm(form);

      // Load the form definition
      console.log('Loading form instances from district endpoint...');
      const instancesResponse = await axios.get('/api/form-instances/district');
      console.log('Form instances response:', instancesResponse.data);
      const allInstances = instancesResponse.data || [];
      const formInstanceData = allInstances.find((inst) => inst.id === form.form_instance_id);
      console.log('Found form instance data:', formInstanceData);

      if (!formInstanceData) {
        console.error('Form definition not found for form_instance_id:', form.form_instance_id);
        setError('Form definition not found');
        return;
      }

      // Create form definition from the instance
      const formDefinition = {
        sections: [{
          section_name: formInstanceData.instance_name || formInstanceData.master_form_name,
          section_note: formInstanceData.instance_description || null,
          fields: []
        }]
      };

      // Load form fields from master form data
      if (formInstanceData.master_form_data) {
        let parsedFormData;
        if (typeof formInstanceData.master_form_data === 'string') {
          try {
            parsedFormData = JSON.parse(formInstanceData.master_form_data);
          } catch (parseError) {
            console.error('Failed to parse master form data:', parseError);
            setError('Form definition is corrupted');
            return;
          }
        } else if (typeof formInstanceData.master_form_data === 'object') {
          parsedFormData = formInstanceData.master_form_data;
        }

        if (parsedFormData && parsedFormData.sections) {
          console.log('Form has', parsedFormData.sections.length, 'sections');

          // Preserve sections instead of flattening - this maintains section names and structure
          // Convert sections with allows_multiple to repeatable_group fields
          const processedSections = parsedFormData.sections.map((section, sectionIndex) => {
            // Handle sections with allows_multiple (repeating groups like siblings)
            if (section.allows_multiple) {
              // Create a repeatable_group field for this section
              // The stored data might have the form_instance_id prefix, so we need to check both
              const baseName = `repeating_group_${sectionIndex}`;
              const prefixedName = form.form_instance_id ? `${form.form_instance_id}_repeating_group_${sectionIndex}` : baseName;
              
              const repeatableField = {
                name: baseName, // Use base name (without prefix) for the field definition
                label: section.section_name,
                type: 'repeatable_group',
                allows_multiple: true,
                min_items: 0,
                max_items: 10,
                fields: section.fields || [],
                // Store both possible names for matching
                _possibleNames: [baseName, prefixedName]
              };
              
              return {
                section_name: section.section_name,
                section_note: section.section_note,
                fields: [repeatableField]
              };
            }
            
            // Regular section - keep as is
            return {
              section_name: section.section_name,
              section_note: section.section_note,
              conditional: section.conditional,
              fields: section.fields || []
            };
          });

          formDefinition.sections = processedSections;
          console.log('Processed sections:', processedSections.length);
        }
      }

      // Extract all field names from form definition for filtering
      // Include both top-level fields and fields within repeatable groups
      const validFieldNames = new Set();
      if (formDefinition && formDefinition.sections) {
        formDefinition.sections.forEach(section => {
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach(field => {
              if (field.name) {
                validFieldNames.add(field.name);
              }
              // Also add nested fields from repeatable groups
              if (field.type === 'repeatable_group' && field.fields) {
                field.fields.forEach(subField => {
                  if (subField.name) {
                    // For repeatable groups, the data is stored under the group name as an array
                    // Each item in the array has the sub-field names
                    // So we don't need to add sub-field names to validFieldNames
                  }
                });
              }
            });
          }
        });
      }
      
      // Also check for repeatable group data that might be stored with different naming patterns
      // Look for keys that match repeating_group_* pattern
      if (form.form_data && typeof form.form_data === 'object') {
        Object.keys(form.form_data).forEach(key => {
          if (key.startsWith('repeating_group_') || key.match(/^[a-f0-9-]+_repeating_group_\d+$/i)) {
            validFieldNames.add(key);
          }
        });
      }

      // Load existing form data, but filter to only include fields that belong to this form
      // This removes corrupted/extra fields from previous bugs
      const existingData = {};
      if (form.form_data && typeof form.form_data === 'object') {
        // First, find all repeatable group fields in the definition and map them by section index AND section name
        const repeatableGroupMap = new Map(); // Maps section index -> field name
        const repeatableGroupByNameMap = new Map(); // Maps section name -> field name
        const allRepeatableGroupFields = [];
        
        if (formDefinition && formDefinition.sections) {
          formDefinition.sections.forEach((section, sectionIndex) => {
            section.fields?.forEach(field => {
              if (field.type === 'repeatable_group') {
                repeatableGroupMap.set(sectionIndex, field.name);
                allRepeatableGroupFields.push(field);
                // Also map by section name for fallback matching
                if (section.section_name) {
                  const normalizedName = section.section_name.toLowerCase().trim();
                  repeatableGroupByNameMap.set(normalizedName, field.name);
                  // Also try matching "Siblings" -> "siblings"
                  if (normalizedName.includes('sibling')) {
                    repeatableGroupByNameMap.set('siblings', field.name);
                  }
                }
              }
            });
          });
        }
        
        console.log(`[EditStudentForm] Found ${allRepeatableGroupFields.length} repeatable group fields in definition`);
        console.log(`[EditStudentForm] Repeatable group map:`, Array.from(repeatableGroupMap.entries()));
        console.log(`[EditStudentForm] All form data keys:`, Object.keys(form.form_data));
        console.log(`[EditStudentForm] Form data keys with 'repeating':`, Object.keys(form.form_data).filter(k => k.toLowerCase().includes('repeat')));
        console.log(`[EditStudentForm] Form data keys with 'sibling':`, Object.keys(form.form_data).filter(k => k.toLowerCase().includes('sibling')));
        // Check for array values that might be repeatable group data
        const arrayValues = Object.entries(form.form_data).filter(([k, v]) => Array.isArray(v) && v.length > 0);
        console.log(`[EditStudentForm] Array values in form_data:`, arrayValues.map(([k, v]) => ({ 
          key: k, 
          length: v.length, 
          sample: v[0],
          sampleKeys: v[0] && typeof v[0] === 'object' ? Object.keys(v[0]) : null,
          hasSiblingName: v[0] && typeof v[0] === 'object' && 'sibling_name' in v[0]
        })));
        // Log the full array value for the first array found
        if (arrayValues.length > 0) {
          console.log(`[EditStudentForm] First array value details:`, {
            key: arrayValues[0][0],
            value: arrayValues[0][1],
            fullValue: JSON.stringify(arrayValues[0][1], null, 2)
          });
        }
        
        
        Object.entries(form.form_data).forEach(([key, value]) => {
          // Always include metadata fields (prefixed with _)
          if (key.startsWith('_')) {
            existingData[key] = value;
          }
          // Include repeatable group fields (they might have UUID prefixes or sectionIndex-based names)
          else if (key.includes('repeating_group_')) {
            // Try multiple matching strategies
            let matchedFieldName = null;
            
            // Strategy 1: Extract section index from key and match to definition
            const match = key.match(/repeating_group_(\d+)/);
            if (match) {
              const sectionIndex = parseInt(match[1]);
              matchedFieldName = repeatableGroupMap.get(sectionIndex);
              console.log(`[EditStudentForm] Strategy 1: Key "${key}" has section index ${sectionIndex}, matched to: ${matchedFieldName || 'none'}`);
            }
            
            // Strategy 2: Try direct match with field name (including prefixed versions)
            if (!matchedFieldName) {
              const matchingField = formDefinition.sections
                ?.flatMap(s => s.fields || [])
                .find(f => {
                  if (f.type !== 'repeatable_group') return false;
                  // Check exact match
                  if (key === f.name) return true;
                  // Check if key ends with the field name (for prefixed versions)
                  if (key.endsWith('_' + f.name)) return true;
                  // Check _possibleNames if available
                  if (f._possibleNames && f._possibleNames.includes(key)) return true;
                  return false;
                });
              if (matchingField) {
                matchedFieldName = matchingField.name;
                console.log(`[EditStudentForm] Strategy 2: Direct match found for "${key}" -> "${matchedFieldName}"`);
              }
            }
            
            // Strategy 3: If value is an array and we have only one repeatable group, use it
            if (!matchedFieldName && Array.isArray(value) && allRepeatableGroupFields.length === 1) {
              matchedFieldName = allRepeatableGroupFields[0].name;
              console.log(`[EditStudentForm] Strategy 3: Single repeatable group found, using "${matchedFieldName}"`);
            }
            
            // Strategy 4: If value is an array, try to match by checking if it contains sibling data
            if (!matchedFieldName && Array.isArray(value) && value.length > 0) {
              const firstItem = value[0];
              // Check if this looks like sibling data
              if (firstItem && typeof firstItem === 'object' && 'sibling_name' in firstItem) {
                const siblingsField = allRepeatableGroupFields.find(f => 
                  f.label?.toLowerCase().includes('sibling') || 
                  f.fields?.some(subf => subf.name === 'sibling_name')
                );
                if (siblingsField) {
                  matchedFieldName = siblingsField.name;
                  console.log(`[EditStudentForm] Strategy 4: Detected sibling data, matched to "${matchedFieldName}"`);
                }
              }
            }
            
            // Strategy 5: If still no match and value is an array, use first repeatable group as fallback
            if (!matchedFieldName && Array.isArray(value) && allRepeatableGroupFields.length > 0) {
              matchedFieldName = allRepeatableGroupFields[0].name;
              console.log(`[EditStudentForm] Strategy 5: Fallback to first repeatable group "${matchedFieldName}"`);
            }
            
            if (matchedFieldName) {
              console.log(`[EditStudentForm] ✓ Mapped repeatable group key "${key}" (${Array.isArray(value) ? value.length + ' items' : 'non-array'}) to field "${matchedFieldName}"`);
              existingData[matchedFieldName] = value;
            } else {
              // Keep original key if we can't match it - might be valid data
              console.warn(`[EditStudentForm] ⚠ Could not match repeatable group key "${key}" (value type: ${Array.isArray(value) ? 'array[' + value.length + ']' : typeof value}), keeping original`);
              existingData[key] = value;
            }
          }
          // Only include fields that are defined in the form definition
          else if (validFieldNames.has(key)) {
            existingData[key] = value;
          }
        });
        
        // After processing all keys, check for array values that might be repeatable group data
        // but weren't matched because they don't have "repeating_group" in the key name
        if (allRepeatableGroupFields.length > 0) {
          Object.entries(form.form_data).forEach(([key, value]) => {
            // Skip if already processed
            if (existingData.hasOwnProperty(key) || key.startsWith('_')) return;
            
            // If this is an array and looks like repeatable group data
            if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
              const firstItem = value[0];
              
              // Check if this looks like sibling data
              if ('sibling_name' in firstItem) {
                const siblingsField = allRepeatableGroupFields.find(f => 
                  f.label?.toLowerCase().includes('sibling') || 
                  f.fields?.some(subf => subf.name === 'sibling_name')
                );
                if (siblingsField && !existingData[siblingsField.name]) {
                  console.log(`[EditStudentForm] ✓ Found sibling data in key "${key}", mapping to "${siblingsField.name}"`);
                  existingData[siblingsField.name] = value;
                }
              }
              // Check if this matches any repeatable group's field structure
              else {
                for (const repeatableField of allRepeatableGroupFields) {
                  if (repeatableField.fields && repeatableField.fields.length > 0) {
                    const expectedFieldNames = repeatableField.fields.map(f => f.name);
                    const itemKeys = Object.keys(firstItem);
                    // If the item has fields that match the repeatable group structure
                    const matches = expectedFieldNames.some(fn => itemKeys.includes(fn));
                    if (matches && !existingData[repeatableField.name]) {
                      console.log(`[EditStudentForm] ✓ Found matching repeatable group data in key "${key}", mapping to "${repeatableField.name}"`);
                      existingData[repeatableField.name] = value;
                      break;
                    }
                  }
                }
              }
            }
          });
          
          // Check other submitted forms for siblings data if this form doesn't have it
          const siblingsField = allRepeatableGroupFields.find(f => 
            f.label?.toLowerCase().includes('sibling') || 
            f.fields?.some(subf => subf.name === 'sibling_name')
          );
          
          if (siblingsField && !existingData[siblingsField.name]) {
            // Look for siblings data in other forms for this student
            console.log(`[EditStudentForm] No siblings data found in this form, checking other forms...`);
            for (const otherForm of submittedForms) {
              if (otherForm.id === form.id) continue; // Skip current form
              
              if (otherForm.form_data && typeof otherForm.form_data === 'object') {
                // Look for repeatable group keys that might contain siblings
                const otherFormKeys = Object.keys(otherForm.form_data);
                const repeatableKeys = otherFormKeys.filter(k => k.includes('repeating_group'));
                
                for (const key of repeatableKeys) {
                  const value = otherForm.form_data[key];
                  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                    if ('sibling_name' in value[0]) {
                      console.log(`[EditStudentForm] ✓ Found siblings data in ${otherForm.instance_name || otherForm.master_form_name}, copying to current form`);
                      existingData[siblingsField.name] = value;
                      break;
                    }
                  }
                }
                
                // Also check for any array values that contain sibling_name
                for (const [key, value] of Object.entries(otherForm.form_data)) {
                  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'sibling_name' in value[0]) {
                    console.log(`[EditStudentForm] ✓ Found siblings data in ${otherForm.instance_name || otherForm.master_form_name} (key: ${key}), copying to current form`);
                    existingData[siblingsField.name] = value;
                    break;
                  }
                }
              }
              
              // If we found siblings data, stop looking
              if (existingData[siblingsField.name]) break;
            }
          }
        }
      }

      console.log(`[EditStudentForm] Loaded form data: ${Object.keys(existingData).length} valid fields (from ${form.form_data ? Object.keys(form.form_data).length : 0} total)`);

      setCurrentFormDefinition(formDefinition);
      setCurrentFormData(existingData);
      // Use form.id (form_submission_students.id) not form.submission_id (form_submission_id)
      // form.id is the specific form record, form.submission_id is shared by multiple forms
      setCurrentSubmissionId(form.id);

    } catch (error) {
      console.error('Failed to load form:', error);
      setError('Failed to load form for editing');
    } finally {
      setLoading(false);
    }
  };

  // Extract all field names from form definition
  const getAllFieldNamesFromDefinition = (formDefinition) => {
    const fieldNames = new Set();
    if (formDefinition && formDefinition.sections) {
      formDefinition.sections.forEach(section => {
        if (section.fields && Array.isArray(section.fields)) {
          section.fields.forEach(field => {
            if (field.name) {
              fieldNames.add(field.name);
            }
          });
        }
      });
    }
    return fieldNames;
  };

  // Handle saving edited form changes
  const saveEditedForm = async (formData) => {
    if (!selectedForm || !currentSubmissionId || !currentFormDefinition) return;

    try {
      setSaving(true);
      setError('');
      setSaveFeedback(null);

      // Get all valid field names from the form definition
      const validFieldNames = getAllFieldNamesFromDefinition(currentFormDefinition);
      
      // Filter formData to only include fields that exist in the form definition
      // This prevents saving corrupted/extra fields from previous bugs
      const filteredFormData = {};
      Object.keys(formData).forEach(key => {
        // Always include metadata fields (prefixed with _)
        if (key.startsWith('_')) {
          filteredFormData[key] = formData[key];
        } 
        // Only include fields that are defined in the form definition
        else if (validFieldNames.has(key)) {
          filteredFormData[key] = formData[key];
        }
      });

      console.log(`[EditStudentForm] Filtered form data: ${Object.keys(filteredFormData).length} fields (was ${Object.keys(formData).length})`);
      console.log(`[EditStudentForm] Valid field names: ${validFieldNames.size}`);

      // Capture submission metadata
      const clientMetadata = await getClientMetadata();
      let ipAddress = null;
      try {
        const ipResponse = await axios.get('/api/auth/client-info');
        ipAddress = ipResponse.data.ipAddress || null;
      } catch (error) {
        console.warn('Could not fetch IP address for submission metadata:', error);
      }

      // Enhance form data with submission metadata
      const enhancedFormData = {
        ...filteredFormData,
        _submissionMetadata: {
          ...clientMetadata,
          ipAddress,
          submittedAt: new Date().toISOString(),
          submittedBy: 'district_admin', // Role identifier
          formInstanceId: selectedForm.form_instance_id,
          submissionType: 'edit'
        }
      };

      // Use /student-form/:id endpoint which updates the specific form_submission_students record
      // NOT /update/:submissionId which would update ALL forms sharing that submission_id
      await axios.put(`/api/form-submissions/student-form/${currentSubmissionId}`, {
        form_data: enhancedFormData
      });
      setSaveFeedback({
        variant: 'success',
        title: 'Saved',
        message: 'Form changes were saved successfully.'
      });

      // Refresh the submitted forms list
      const formsResponse = await axios.get(`/api/student-intake/district/${studentIntakeId}/forms`);
      setSubmittedForms(formsResponse.data || []);

    } catch (error) {
      console.error('Failed to save form:', error);
      setSaveFeedback({
        variant: 'error',
        title: 'Save failed',
        message: error.response?.data?.error || 'Failed to save form'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle opening modal to add new form
  const openAddFormModal = async (form) => {
    try {
      setLoading(true);

      // Load the form definition for the modal
      const instancesResponse = await axios.get('/api/form-instances/district');
      const allInstances = instancesResponse.data || [];
      const formInstanceData = allInstances.find((inst) => inst.id === form.id);

      if (!formInstanceData) {
        setError('Form definition not found');
        return;
      }

      // Create form definition from the instance
      const formDefinition = {
        sections: [{
          section_name: formInstanceData.instance_name || formInstanceData.master_form_name,
          section_note: formInstanceData.instance_description || null,
          fields: []
        }]
      };

      // Load form fields from master form data
      if (formInstanceData.master_form_data) {
        let parsedFormData;
        if (typeof formInstanceData.master_form_data === 'string') {
          try {
            parsedFormData = JSON.parse(formInstanceData.master_form_data);
          } catch (parseError) {
            console.error('Failed to parse master form data:', parseError);
            setError('Form definition is corrupted');
            return;
          }
        } else if (typeof formInstanceData.master_form_data === 'object') {
          parsedFormData = formInstanceData.master_form_data;
        }

        if (parsedFormData && parsedFormData.sections) {
          console.log('Form has', parsedFormData.sections.length, 'sections');

          // For modal, combine all sections into one
          const allFields = [];
          parsedFormData.sections.forEach((section, index) => {
            console.log(`Section ${index}: ${section.section_name}, ${section.fields?.length || 0} fields`);
            if (section.fields) {
              allFields.push(...section.fields);
            }
          });

          formDefinition.sections[0].fields = allFields;
          console.log('Total loaded fields:', allFields.length);
        }
      }

      setModalFormInstance(form);
      setModalFormDefinition(formDefinition);
      setModalFormData({});
      setShowAddFormModal(true);

    } catch (error) {
      console.error('Failed to load form for modal:', error);
      setError('Failed to open form');
    } finally {
      setLoading(false);
    }
  };

  // Handle submitting the form from the modal
  const submitModalForm = async () => {
    if (!modalFormInstance || !modalFormDefinition) return;

    // Capture submission metadata
    const clientMetadata = await getClientMetadata();
    let ipAddress = null;
    try {
      const ipResponse = await axios.get('/api/auth/client-info');
      ipAddress = ipResponse.data.ipAddress || null;
    } catch (error) {
      console.warn('Could not fetch IP address for submission metadata:', error);
    }

    // Debug: Log the form data being submitted
    console.log('[EditStudentForm] Submitting modal form data:', JSON.stringify(modalFormData, null, 2));
    console.log('[EditStudentForm] Checking for residence_coordinates:', modalFormData.residence_coordinates);

    // Enhance form data with submission metadata
    const enhancedFormData = {
      ...modalFormData,
      _submissionMetadata: {
        ...clientMetadata,
        ipAddress,
        submittedAt: new Date().toISOString(),
        submittedBy: 'district_admin', // Role identifier
        formInstanceId: modalFormInstance.id,
        submissionType: 'updated'
      }
    };

    if (
      modalFormDefinition &&
      !confirmUngeocodedMapAddressStandaloneIfNeeded(modalFormDefinition, modalFormData, modalFormInstance)
    ) {
      return;
    }

    try {
      setSavingModal(true);
      setError('');
      setSaveFeedback(null);

      await axios.post('/api/form-submissions/create-submission', {
        submission_data: enhancedFormData,
        form_instance_id: modalFormInstance.id,
        student_intake_id: studentIntakeId
      });

      // Refresh the forms list
      const formsResponse = await axios.get(`/api/student-intake/district/${studentIntakeId}/forms`);
      setSubmittedForms(formsResponse.data || []);

      const formLabel = modalFormInstance.instance_name || modalFormInstance.master_form_name;
      setShowAddFormModal(false);
      setModalFormInstance(null);
      setModalFormDefinition(null);
      setModalFormData({});
      setSaveFeedback({
        variant: 'success',
        title: 'Submitted',
        message: `${formLabel} was submitted successfully.`
      });

    } catch (error) {
      console.error('Failed to submit form from modal:', error);

      if (error.response?.status === 409 && error.response.data.can_overwrite) {
        // Duplicate submission - offer overwrite option
        const shouldOverwrite = window.confirm(
          `${error.response.data.error}\n\n⚠️  Warning: This will overwrite the existing submission with your current data.\n\nDo you want to continue and overwrite?`
        );

        if (shouldOverwrite) {
          // Overwrite the existing submission
          try {
            await axios.put(`/api/form-submissions/overwrite-submission/${error.response.data.existing_submission_id}`, {
              submission_data: enhancedFormData,
              student_intake_id: studentIntakeId
            });

            const owLabel = modalFormInstance.instance_name || modalFormInstance.master_form_name;
            const formsResponse = await axios.get(`/api/student-intake/district/${studentIntakeId}/forms`);
            setSubmittedForms(formsResponse.data || []);

            setShowAddFormModal(false);
            setModalFormInstance(null);
            setModalFormDefinition(null);
            setModalFormData({});
            setSaveFeedback({
              variant: 'success',
              title: 'Updated',
              message: `${owLabel} was updated successfully.`
            });
          } catch (overwriteError) {
            console.error('Failed to overwrite submission:', overwriteError);
            setSaveFeedback({
              variant: 'error',
              title: 'Overwrite failed',
              message: overwriteError.response?.data?.error || 'Could not overwrite the existing submission.'
            });
          }
        }
        // If user cancels, stay in modal with form data intact
      } else if (error.response?.status === 409) {
        // Duplicate submission without overwrite option
        setSaveFeedback({
          variant: 'error',
          title: 'Cannot submit',
          message: error.response.data.error || 'This form cannot be submitted right now.'
        });
      } else {
        setSaveFeedback({
          variant: 'error',
          title: 'Submit failed',
          message: error.response?.data?.error || 'Failed to submit form'
        });
      }
    } finally {
      setSavingModal(false);
    }
  };

  // Handle closing the modal
  const closeAddFormModal = () => {
    setShowAddFormModal(false);
    setModalFormInstance(null);
    setModalFormDefinition(null);
    setModalFormData({});
  };

  // Go back to form list
  const backToFormList = () => {
    setSelectedForm(null);
    setCurrentFormDefinition(null);
    setCurrentFormData({});
    setCurrentSubmissionId(null);
    setError('');
    setSaveFeedback(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="edit-student-form">
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => navigate('/district-admin/students')}
          style={{
            background: 'none',
            border: 'none',
            color: '#007bff',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '0'
          }}
        >
          ← Back to Students
        </button>
      </div>

      <div style={{
        background: '#fff3cd',
        color: '#856404',
        padding: '0.75rem 1.5rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        border: '1px solid #ffeaa7'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
          Edit Forms - {studentIntake ? `${studentIntake.first_name} ${studentIntake.last_name}` : 'Student'}
        </div>
        <div>Select a form below to edit its data individually.</div>
      </div>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {!selectedForm ? (
        // Form Selection View
        <div>
          <h3>Submitted Forms</h3>
          {submittedForms.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
              {submittedForms.map((form, index) => (
                <div key={index} style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: '#f9f9f9'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>
                        {form.instance_name || form.master_form_name}
                      </h4>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        Submitted: {new Date(form.submitted_at).toLocaleDateString()}
                        {form.form_data && ` • ${Object.keys(form.form_data).length} fields`}
                      </div>
                    </div>
                    <button
                      onClick={() => selectForm(form)}
                      style={{
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit Form
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No submitted forms found for this student.</p>
          )}

          <h3>Add New Form</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {availableForms
              .filter(form => !submittedForms.some(submitted => submitted.form_instance_id === form.id))
              .map((form, index) => (
                <div key={index} style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: '#fff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>
                        {form.instance_name || form.master_form_name}
                      </h4>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {form.instance_description || 'No description available'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!loading) {
                          openAddFormModal(form);
                        }
                      }}
                      disabled={loading}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      Add Form
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        // Individual Form Editing View
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <button
              onClick={() => backToFormList()}
              style={{
                background: 'none',
                border: '1px solid #007bff',
                color: '#007bff',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ← Back to Forms
            </button>
            <h3 style={{ margin: 0 }}>
              Editing: {selectedForm.instance_name || selectedForm.master_form_name}
            </h3>
          </div>

          {currentFormDefinition && (
            <FormRenderer
              formData={currentFormDefinition}
              formValues={currentFormData}
              onChange={setCurrentFormData}
              userRole="district_admin"
            />
          )}

          <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
            <button
              onClick={() => saveEditedForm(currentFormData)}
              disabled={saving}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Add Form Modal */}
      {showAddFormModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>
                Add {modalFormInstance?.instance_name || modalFormInstance?.master_form_name}
              </h3>
              <button
                onClick={closeAddFormModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {modalFormDefinition && (
                <FormRenderer
                  formData={modalFormDefinition}
                  formValues={modalFormData}
                  onChange={setModalFormData}
                  userRole="district_admin"
                />
              )}

              <div style={{
                marginTop: '2rem',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={closeAddFormModal}
                  disabled={savingModal}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: savingModal ? 'not-allowed' : 'pointer',
                    opacity: savingModal ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitModalForm}
                  disabled={savingModal}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: savingModal ? 'not-allowed' : 'pointer',
                    opacity: savingModal ? 0.6 : 1
                  }}
                >
                  Submit Form
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SuccessPopup
        show={!!saveFeedback}
        onClose={() => setSaveFeedback(null)}
        variant={saveFeedback?.variant || 'success'}
        title={saveFeedback?.title}
        message={saveFeedback?.message || ''}
        autoCloseDelay={saveFeedback?.variant === 'error' ? 0 : 5000}
      />
    </div>
  );
};

export default EditStudentForm;
